import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import { useTranslation } from 'react-i18next'
import { httpClient, apiUrl } from './admin/dataProvider'
import authProvider from './admin/authProvider'

const Setup2FAPage: React.FC = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [verifying, setVerifying] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [secret, setSecret] = useState('')
  const [qrCode, setQrCode] = useState('')
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)
  const [error, setError] = useState('')

  const navigate = useNavigate()

  const didCall = useRef(false)
  useEffect(() => {
    if (didCall.current) return
    didCall.current = true

    const setup2FA = async () => {
      try {
        setLoading(true)
        const { json } = await httpClient(`${apiUrl}/2fa/setup`, {
          method: 'POST',
        })

        setSecret(json.secret)
        setQrCode(json.provisioning_uri)

        // Generate QR code using qrcode library
        try {
          QRCode.toDataURL(json.provisioning_uri, {
            width: 200,
            margin: 2,
            errorCorrectionLevel: 'M',
          })
            .then((dataUrl) => {
              setQrCodeDataUrl(dataUrl)
            })
            .catch((qrError) => {
              console.error('Error generating QR code:', qrError)
              // If QR code generation fails, we'll still show the secret key
            })
        } catch (qrError) {
          console.error('Error in QR code generation setup:', qrError)
          // If QR code generation setup fails, we'll still show the secret key
        }

        setLoading(false)
        // eslint-disable-next-line @typescript-eslint/no-shadow
      } catch (error) {
        console.error('Error setting up 2FA:', error)
        const status = (error as any)?.status
        const message = (error as any)?.message
        if (status === 401 || message === 'Unauthorized') {
          // Unauthorized: bounce back to the login page
          window.location.replace('/admin/login')
          return
        }
        setError(t('twofa.error_setup_failed'))
        setLoading(false)
      }
    }

    setup2FA()
  }, [t])

  const handleVerify = async () => {
    if (!verificationCode) {
      setError(t('twofa.error_enter_code'))
      return
    }

    try {
      setVerifying(true)
      const { json } = await httpClient(`${apiUrl}/2fa/verify`, {
        method: 'POST',
        body: JSON.stringify({
          code: verificationCode,
          remember_device: rememberDevice,
        }),
      })

      if (json.success) {
        // Redirect to admin dashboard
        navigate('/admin')
      } else {
        setError(json.message || t('twofa.error_invalid_code'))
        setVerifying(false)
      }
    } catch (caughtError) {
      console.error('Error verifying 2FA code:', caughtError)
      setError(t('twofa.error_verify_failed'))
      setVerifying(false)
    }
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{
        backgroundColor: '#f5f5f5',
        backgroundImage: 'url(/logo192.png)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center 40px',
        backgroundSize: '100px',
      }}
    >
      <Box mb={4} mt={-10}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Procurato - Spendtool
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 600, width: '100%', p: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('twofa.setup_title')}
          </Typography>

          <Typography variant="body1" paragraph>
            {t('twofa.setup_intro')}
          </Typography>

          <Typography variant="body1" component="ol">
            <li>{t('twofa.setup_step_1')}</li>
            <li>{t('twofa.setup_step_2')}</li>
            <li>{t('twofa.setup_step_3')}</li>
          </Typography>

          <Box my={3} display="flex" justifyContent="center">
            {qrCodeDataUrl ? (
              <img
                src={qrCodeDataUrl}
                alt={t('twofa.qr_code_alt')}
                style={{ width: 200, height: 200 }}
              />
            ) : qrCode ? (
              <Typography color="error">{t('twofa.qr_code_error')}</Typography>
            ) : null}
          </Box>

          <Typography variant="body2" paragraph>
            {t('twofa.manual_entry')}
          </Typography>

          <Typography
            variant="body1"
            component="div"
            sx={{
              backgroundColor: '#f5f5f5',
              p: 1,
              borderRadius: 1,
              fontFamily: 'monospace',
              textAlign: 'center',
              mb: 3,
            }}
          >
            {secret}
          </Typography>

          <TextField
            label={t('twofa.verification_code_label')}
            variant="outlined"
            fullWidth
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            error={!!error}
            helperText={error}
            placeholder={t('twofa.verification_code_placeholder')}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
            }
            label={t('twofa.remember_device')}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleVerify}
            disabled={verifying || loggingOut}
          >
            {verifying ? (
              <CircularProgress size={24} />
            ) : (
              t('twofa.verify_enable_button')
            )}
          </Button>

          <Box mt={2}>
            <Button
              variant="text"
              color="secondary"
              fullWidth
              onClick={async () => {
                if (loggingOut) return
                setLoggingOut(true)
                try {
                  await authProvider.logout({})
                } catch (e) {
                  // ignore logout errors; proceed to login page
                } finally {
                  window.location.replace('/admin/login')
                }
              }}
              disabled={loggingOut || verifying}
            >
              {loggingOut ? <CircularProgress size={24} /> : t('twofa.logout')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default Setup2FAPage
